// src/components/profiles/Member/components/posts/pray/PrayerMyGrid.tsx

"use client";

import React, { useEffect, useCallback, useRef } from "react";
import { useSearchParams, usePathname } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/navigation";

import type { AppDispatch } from "../../../../../../redux/storeTypes";

import {
  fetchMyPrayers,
} from "../../../../../../redux/ugc/prayerSlice";

import {
  selectMyPrayers,
} from "../../../../../../redux/selectors/ugc/prayerSelectors";

import { Loader } from "../../../../../common";
import PrayerGridItem from "./PrayerGridItem";
import PrayerEmptyState from "./PrayerEmptyState";

import "./PrayerMyGrid.css";

const PrayerMyGrid: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();

  const prayers = useSelector(selectMyPrayers);
  const loading = useSelector((state: any) => state.prayers.me.loading);
  const hasMore = useSelector((state: any) => state.prayers.me.hasMore);

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const searchParams = useSearchParams();
  const pathname = usePathname();

  const focusedOnceRef = useRef<string | null>(null);

  // -------------------------------------------------
  // Initial fetch (page=1)
  // -------------------------------------------------
  useEffect(() => {
    dispatch(fetchMyPrayers({ page: 1 }));
  }, [dispatch]);

  // -------------------------------------------------
  // Create navigation
  // -------------------------------------------------
  const handleCreate = useCallback(() => {
    router.push("/prayers/create");
  }, [router]);

  // -------------------------------------------------
  // Load next page (guarded)
  // -------------------------------------------------
  const loadNext = useCallback(() => {
    if (loading) return;
    if (!hasMore) return;
    dispatch(fetchMyPrayers());
  }, [dispatch, loading, hasMore]);

  // -------------------------------------------------
  // Infinite scroll observer
  // -------------------------------------------------
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        loadNext();
      },
      {
        root: null,
        rootMargin: "300px 0px",
        threshold: 0,
      }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [loadNext]);


  // -------------------------------------------------
  // Scroll to focused prayer
  // -------------------------------------------------
  useEffect(() => {
    const focusSlug = searchParams?.get("focusPrayer");
    if (!focusSlug) return;

    if (focusedOnceRef.current === focusSlug) return;
    if (!prayers.length) return;

    const run = () => {
      const el = document.querySelector(
        `[data-prayer-slug="${CSS.escape(focusSlug)}"]`
      ) as HTMLElement | null;

      if (!el) return;

      focusedOnceRef.current = focusSlug;

      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });

      // clean query param after successful scroll
      const params = new URLSearchParams(searchParams.toString());
      params.delete("focusPrayer");

      const next = params.toString();
      window.history.replaceState({}, "", next ? `${pathname}?${next}` : pathname);
    };

    const id = window.requestAnimationFrame(run);
    return () => window.cancelAnimationFrame(id);
  }, [prayers, searchParams, pathname]);


  // -------------------------------------------------
  // Initial loading state
  // -------------------------------------------------
  if (loading && prayers.length === 0) {
    return (
      <div className="prayer-my-grid__loading">
        <Loader variant="dots" size="md" color="glory" />
      </div>
    );
  }

  // -------------------------------------------------
  // Empty state
  // -------------------------------------------------
  if (!loading && prayers.length === 0) {
    return <PrayerEmptyState onCreate={handleCreate} />;
  }

  return (
    <section className="prayer-my-grid" aria-label="My Prayers">
      <div className="prayer-my-grid__list">
        {prayers.map((prayer) => (
          <PrayerGridItem key={prayer.slug} prayer={prayer} />
        ))}
      </div>

      {/* Sentinel */}
      <div ref={sentinelRef} className="prayer-my-grid__sentinel" />

      {/* Bottom loader */}
      {loading && prayers.length > 0 && (
        <div className="prayer-my-grid__more">
          <Loader variant="dots" size="md" color="glory" />
        </div>
      )}

      {/* End hint */}
      {!hasMore && prayers.length > 0 && (
        <div className="prayer-my-grid__endHint">
          You’re all caught up on Prayers ✨
        </div>
      )}
    </section>
  );
};

export default PrayerMyGrid;